<script setup>
import { reactive, ref, unref, toRaw, computed, provide } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import ArrowDownSvg from '@/assets/arrow-down.svg?skipsvgo'
import GlobeSvg from '@/assets/globe.svg?skipsvgo'
// import { useMediaFilterStore } from '../stores/mediaFilterStore'
// import { storeToRefs } from 'pinia'


const props = defineProps({
    storeKey: { type: String, default: 'options' },
    synced: { type: Boolean, default: false },
    placeholder: { type: String, required: true },
    // dynamicPlaceholder: { type: Boolean, default: true },
})

const emit = defineEmits(['change'])

const isOpen = ref(false)

const options = ref([]);
// let options;
// const store = useMediaFilterStore()
// if (props.synced) {
//     options = storeToRefs(store)[props.storeKey]
// } else {
//     options = ref([])
// }

const selectedOption = computed(() => options.value.find(({ selected }) => selected))
const computedValue = computed(() => {
    // if (!props.dynamicPlaceholder) { return props.placeholder }

    if (selectedOption.value == null | selectedOption.value?.value == undefined) {
        return props.placeholder
    }
    return selectedOption.value.label ?? selectedOption.value.value
});

function registerOption(option) {
    options.value.push(option)
}
function unregisterOption(option) {
    options.value = options.value.filter(({ uid }) => uid !== option.uid)
}
function select(uid) {
    options.value.forEach((option) => {
        if (option.uid === uid) {
            option.selected = true
        } else {
            option.selected = false
        }
    })
    emit('change', toRaw(unref(selectedOption)));
    close()
}
provide('dropdown', { select, registerOption, unregisterOption });
function open()   { isOpen.value = true }
function close()  { isOpen.value = false }
function toggle() { isOpen.value = !isOpen.value }
</script>

<template>
    <div
        v-on-click-outside="close"
        :class="['dropdown', { 'dropdown--open': isOpen }]"
    >
        <div
            class="dropdown__header"
            @click="toggle"
        >
            <GlobeSvg class="dropdown__globe-icon" />
            <span>{{ computedValue }}</span>
            <ArrowDownSvg class="dropdown__icon" />
        </div>

        <div class="dropdown__dropdown">
            <transition name="drop-in">
                <div
                    v-show="isOpen"
                    class="dropdown__options"
                >
                    <slot></slot>
                </div>
            </transition>
        </div>
    </div>
</template>

<style lang="scss">
@use "@/scss/framework" as *;

@layer components.dropdown {

    :root {
        --dropdown-color: var(--color-shark-60);
        --dropdown-color--open: var(--color-white);
        --dropdown-color--hover: var(--color-white);
        --dropdown-color--open--hover: var(--color-white);
        --dropdown-icon-color: var(--dropdown-color);
        --dropdown-icon-color--open: var(--dropdown-color--open);
        --dropdown-icon-color--hover: var(--dropdown-color--hover);
        --dropdown-icon-color--open--hover: var(--dropdown-color--open--hover);
        --dropdown-globe-icon-color: var(--dropdown-color);
        --dropdown-globe-icon-color--open: var(--dropdown-color--open);
        --dropdown-globe-icon-color--hover: var(--dropdown-color--hover);
        --dropdown-globe-icon-color--open--hover: var(--dropdown-color--open--hover);

        --dropdown-options-background-color: var(--color-black);
        --dropdown-option-background-color--hover: var(--color-shark-60);
        --dropdown-option-color: var(--color-white);
        --dropdown-option-color--hover: var(--color-white);

        --dropdown-selected-color: var(--color-white);
        --dropdown-selected-background-color: var(--color-kabul-100);
        --dropdown-selected-background-color--hover: var(--color-kabul-100);
    }

    .dropdown {
        --_transition: var(--duration-slow) var(--easing-default);

        display: inline-flex;
        flex-flow: row nowrap;
        align-items: center;
        gap: 0;
        position: relative;
        padding: 0;
        font: var(--font-body-1);

        &__header {
            position: relative;
            cursor: pointer;
            color: var(--dropdown-color);
            display: inline-flex;
            align-items: center;
            gap: var(--size-10);
            z-index: 1;
            transition: color var(--_transition);
            padding: 6px 0;


            .dropdown &:hover {
                @media (hover: hover) and (pointer: fine) {
                    color: var(--dropdown-color--hover);

                    .dropdown__globe-icon {
                        color: var(--dropdown-globe-icon-color--hover);
                    }
                    .dropdown__icon {
                        color: var(--dropdown-icon-color--hover);
                    }
                }
            }
            .dropdown--open & {
                color: var(--dropdown-color--open);
            }
            .dropdown--open &:hover {
                @media (hover: hover) and (pointer: fine) {
                    color: var(--dropdown-color--open--hover);

                    .dropdown__globe-icon {
                        color: var(--dropdown-globe-icon-color--open--hover);
                    }
                    .dropdown__icon {
                        color: var(--dropdown-icon-color--open--hover);
                    }
                }
            }
        }

        &__dropdown {
            position: absolute;
            left: 0;
            top: 100%;
        }

        &__globe-icon {
            @include svg-auto-color;
            height: 18px;
            width: 18px;
            transition:
                transform var(--_transition),
                color var(--_transition)
            ;
            color: var(--dropdown-globe-icon-color);

            .dropdown--open & {
                color: var(--dropdown-globe-icon-color--open);
            }
        }

        &__icon {
            width: auto;
            height: 0.384em;
            width: 0.6865em;
            transition:
                transform var(--_transition),
                color var(--_transition)
            ;
            color: var(--dropdown-icon-color);

            .dropdown--open & {
                color: var(--dropdown-icon-color--open);
            }

            @include svg-auto-color;
        }

        &__options {
            position: relative;
            z-index: 2;
            background-color: var(--dropdown-options-background-color);

            display: flex;
            flex-flow: column;
        }

        &__option {
            cursor: pointer;
            padding: var(--size-6) 28px;
            white-space: nowrap;
            color: var(--dropdown-option-color);

            @include hover {
                color: var(--dropdown-option-color--hover);
                background-color: var(--dropdown-option-background-color--hover);
            }

            &-selected {
                color: var(--dropdown-selected-color);
                background-color: var(--dropdown-selected-background-color);
                @include hover {
                    background-color: var(--dropdown-selected-background-color--hover);
                }
            }
        }

        &--open {
            .dropdown__header { }
            .dropdown__icon {
                transform: rotateZ(180deg);
            }
        }
    }

    .drop-in-enter-active {
        transform-origin: top;
        animation: drop-in var(--duration-slow) var(--easing-decelerate) forwards;
    }

    .drop-in-leave-active {
        transform-origin: top;
        animation: drop-out var(--duration-slow) var(--easing-decelerate) forwards;
    }

    @keyframes drop-in {
        0% { opacity: 0; transform: rotateX(90deg); }
        20% { opacity: 1; }
        100% { opacity: 1; transform: rotateX(0deg); }
    }

    @keyframes drop-out {
        0% { opacity: 1; transform: rotateX(0deg); }
        80% { opacity: 1; }
        100% { opacity: 0; transform: rotateX(90deg); }
    }
}

</style>
